import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Calendar, Star, ChevronDown } from 'lucide-react';
import Layout from '@/components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Holidays({ user, onLogout }) {
  const [holidays, setHolidays] = useState([]);
  const [year, setYear] = useState(new Date().getFullYear());
  const [filter, setFilter] = useState('all');
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ date: '', name_is: '', name_en: '', holiday_type: 'public_holiday', start_time: '00:00', end_time: '24:00', is_full_day: true });

  const fetchHolidays = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/holidays?year=${year}`, { headers: { Authorization: `Bearer ${token}` } });
      setHolidays(res.data || []);
    } catch { toast.error('Villa við að sækja helgidaga'); }
  }, [year]);

  useEffect(() => { fetchHolidays(); }, [fetchHolidays]);

  const toggleActive = async (hol) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/holidays/${hol.id}`, { is_active: !hol.is_active }, { headers: { Authorization: `Bearer ${token}` } });
      fetchHolidays();
    } catch { toast.error('Villa'); }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/holidays`, addForm, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Helgidagur bætt við!');
      setAddOpen(false);
      fetchHolidays();
    } catch { toast.error('Villa'); }
  };

  const filtered = holidays.filter(h => filter === 'all' || h.holiday_type === filter);

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="holidays-page">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold">Helgidagar</h1>
          <div className="flex gap-2 items-center">
            <select value={year} onChange={(e) => setYear(parseInt(e.target.value))} className="px-3 py-2 border rounded-md" data-testid="year-select">
              {Array.from({ length: 10 }, (_, i) => 2026 + i).map(y => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
            <Button variant={filter === 'all' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('all')}>Allir</Button>
            <Button variant={filter === 'major_holiday' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('major_holiday')} className={filter === 'major_holiday' ? '' : 'text-red-600'}>
              Stórhátíðar
            </Button>
            <Button variant={filter === 'public_holiday' ? 'default' : 'outline'} size="sm" onClick={() => setFilter('public_holiday')} className={filter === 'public_holiday' ? '' : 'text-blue-600'}>
              Venjulegir
            </Button>
            <Button onClick={() => setAddOpen(true)} data-testid="add-holiday-btn">Bæta við</Button>
          </div>
        </div>

        <div className="grid gap-2">
          {filtered.map((hol) => (
            <Card key={hol.id} className={`${!hol.is_active ? 'opacity-40' : ''} ${hol.holiday_type === 'major_holiday' ? 'border-l-4 border-l-red-500' : 'border-l-4 border-l-blue-500'}`} data-testid={`holiday-${hol.id}`}>
              <CardContent className="py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {hol.holiday_type === 'major_holiday' ? (
                    <Star className="h-4 w-4 text-red-500 fill-red-500" />
                  ) : (
                    <Calendar className="h-4 w-4 text-blue-500" />
                  )}
                  <div>
                    <p className="font-medium">{hol.name_is}</p>
                    <p className="text-xs text-gray-500">
                      {hol.date}
                      {!hol.is_full_day && ` (${hol.start_time}–${hol.end_time})`}
                      {' · '}
                      <span className={hol.holiday_type === 'major_holiday' ? 'text-red-600' : 'text-blue-600'}>
                        {hol.holiday_type === 'major_holiday' ? 'Stórhátíðardagur' : 'Samningsbundinn frídagur'}
                      </span>
                      {hol.is_system_generated && ' · Sjálfvirkt'}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => toggleActive(hol)}
                  className={hol.is_active ? 'text-red-600' : 'text-green-600'}
                  data-testid={`toggle-${hol.id}`}
                >
                  {hol.is_active ? 'Óvirkja' : 'Virkja'}
                </Button>
              </CardContent>
            </Card>
          ))}
          {filtered.length === 0 && (
            <Card><CardContent className="py-8 text-center text-gray-400">Engir helgidagar fundust</CardContent></Card>
          )}
        </div>

        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogContent aria-describedby="add-hol-desc">
            <DialogHeader><DialogTitle>Bæta við helgidegi</DialogTitle></DialogHeader>
            <p id="add-hol-desc" className="sr-only">Nýr helgidagur</p>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label>Dagsetning</Label>
                  <Input type="date" value={addForm.date} onChange={e => setAddForm({...addForm, date: e.target.value})} required data-testid="hol-date" />
                </div>
                <div className="space-y-1">
                  <Label>Tegund</Label>
                  <select value={addForm.holiday_type} onChange={e => setAddForm({...addForm, holiday_type: e.target.value})} className="w-full px-3 py-2 border rounded-md" data-testid="hol-type">
                    <option value="public_holiday">Samningsbundinn frídagur</option>
                    <option value="major_holiday">Stórhátíðardagur</option>
                  </select>
                </div>
              </div>
              <div className="space-y-1">
                <Label>Nafn (ís)</Label>
                <Input value={addForm.name_is} onChange={e => setAddForm({...addForm, name_is: e.target.value})} required data-testid="hol-name" />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={addForm.is_full_day} onChange={e => setAddForm({...addForm, is_full_day: e.target.checked, start_time: e.target.checked ? '00:00' : '12:00', end_time: '24:00'})} className="w-4 h-4" />
                <Label>Allur dagurinn</Label>
                {!addForm.is_full_day && (
                  <div className="flex gap-2 ml-4">
                    <Input type="time" value={addForm.start_time} onChange={e => setAddForm({...addForm, start_time: e.target.value})} className="w-28" />
                    <span className="self-center">–</span>
                    <Input type="time" value={addForm.end_time} onChange={e => setAddForm({...addForm, end_time: e.target.value})} className="w-28" />
                  </div>
                )}
              </div>
              <Button type="submit" className="w-full" data-testid="hol-submit">Vista</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
